import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/notificationSlice';
import Input from '../../components/ui/Input';
import { Link, useNavigate } from 'react-router-dom';
import { setLoading } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store/store';
import robotIllustration from '../../assets/img/Illustration-Login.png';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { helper } from '../../utils/helper';
import { useTranslation } from 'react-i18next';
import LoginLayout from '../../components/layout/LoginLayout';
import { AuthenticationService } from '../../api/generated/services/AuthenticationService';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
  }>({});
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleEmailChange = (value: string) => {
    setEmail(value);
    const error = helper.validateEmail(value);
    setErrors(prev => ({
      ...prev,
      email: error
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const emailError = helper.validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }

    setIsSubmitting(true);
    dispatch(setLoading(true));
    
    try {
      await AuthenticationService.postAppAuthForgotPassword({ email });
      
      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('linkSentToPasswordReset'),
      }));
      navigate('/login');
    } catch (error: any) {
      const errorMessage = error?.body?.error || error?.message || t('password_reset_failed');
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: errorMessage,
      }));
    } finally {
      setIsSubmitting(false);
      dispatch(setLoading(false));
    }
  };

  return (
    <LoginLayout>
      <div className="login-illustration">
        <img src={robotIllustration} alt={t('resetPassword')} />
      </div>

      <Header isLoginHeader={true} showLoginHeader={true} />

      <div className="login-content">
        <div className="login-form-container">
          <div className="login-titles">
            <h1>{t('resetPassword')}</h1>
            <p>{t('forgotPassword')}</p>
          </div>
          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <Input
                id="email"
                label={t('emailAddress')}
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={errors.email}
              />
            </div>
            <button
              type="submit" 
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('notification_loading') : t('resetPassword')}
            </button>
          </form>
          <Link to="/login" className="registration-link">{t('backToLogin')}</Link>
        </div>
      </div>
      <Footer />
    </LoginLayout>
  );
};

export default ForgotPasswordPage;
